import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = {
  get: (path, config) => axios.get(`${API}${path}`, config).then((r) => r.data),
  getBlob: (path) => axios.get(`${API}${path}`, { responseType: "blob" }).then((r) => r.data),
  post: (path, body) => axios.post(`${API}${path}`, body).then((r) => r.data),
  put: (path, body) => axios.put(`${API}${path}`, body).then((r) => r.data),
  patch: (path, body) => axios.patch(`${API}${path}`, body).then((r) => r.data),
  del: (path) => axios.delete(`${API}${path}`).then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  postFile: (path, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}${path}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  downloadBlob: async (path, filename) => {
    const blob = await axios.get(`${API}${path}`, { responseType: "blob" }).then((r) => r.data);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Build an authenticated, displayable URL for a stored file ("/api/files/{id}")
export const fileSrc = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  const token = localStorage.getItem("fsm_token");
  return `${process.env.REACT_APP_BACKEND_URL}${url}?auth=${token}`;
};

export const CATEGORIES = ["mechanical", "electric", "plumbing", "drywall", "window", "landscape", "snow", "general"];
export const SPECIALTIES = ["plumbing", "hvac", "electrical", "carpentry", "general_repair"];
export const specialtyColor = (s) =>
  ({
    plumbing: "bg-cyan-100 text-cyan-700",
    hvac: "bg-orange-100 text-orange-700",
    electrical: "bg-yellow-100 text-yellow-700",
    carpentry: "bg-lime-100 text-lime-700",
    general_repair: "bg-slate-100 text-slate-600",
  }[s] || "bg-slate-100 text-slate-600");
export const CATEGORY_TO_SPECIALTY = {
  mechanical: "hvac", electric: "electrical", plumbing: "plumbing",
  drywall: "carpentry", window: "carpentry", landscape: "general_repair",
  snow: "general_repair", general: "general_repair",
};
export const PRIORITIES = ["low", "medium", "high", "urgent"];
export const STATUSES = ["to_schedule", "to_do", "make_estimate", "estimate_sent", "estimate_approved",
  "in_progress", "waiting_info", "job_done", "completed", "completed_no_charge",
  "blocked", "cancelled", "estimate_rejected"];
export const ROLES = ["admin", "manager", "director", "pm", "technician", "client", "purchasing", "estimator", "analista", "financeiro", "rh", "controller", "subcontractor"];
export const ROLE_LABELS = {
  admin: { en: "Admin", pt: "Admin" },
  manager: { en: "Manager", pt: "Gerente" },
  director: { en: "Director", pt: "Diretor" },
  pm: { en: "Project Manager", pt: "Project Manager" },
  estimator: { en: "Estimator", pt: "Orçamentista" },
  technician: { en: "Technician", pt: "Técnico" },
  purchasing: { en: "Purchasing", pt: "Compras" },
  analista: { en: "Analyst", pt: "Analista" },
  financeiro: { en: "Finance", pt: "Financeiro" },
  rh: { en: "HR", pt: "RH" },
  controller: { en: "Controller", pt: "Controller" },
  client: { en: "Client", pt: "Cliente" },
  subcontractor: { en: "Subcontractor", pt: "Subcontratado" },
};
export const roleLabel = (role, lang = "en") => (ROLE_LABELS[role]?.[lang] || role);

export const statusBadge = (s) =>
  ({
    to_schedule: "bg-amber-100 text-amber-800 border-amber-200",
    to_do: "bg-sky-100 text-sky-800 border-sky-200",
    make_estimate: "bg-indigo-100 text-indigo-800 border-indigo-200",
    estimate_sent: "bg-blue-100 text-blue-800 border-blue-200",
    estimate_approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    in_progress: "bg-cyan-100 text-cyan-800 border-cyan-200",
    waiting_info: "bg-orange-100 text-orange-800 border-orange-200",
    job_done: "bg-teal-100 text-teal-800 border-teal-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    completed_no_charge: "bg-lime-100 text-lime-800 border-lime-200",
    blocked: "bg-red-100 text-red-700 border-red-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
    estimate_rejected: "bg-rose-100 text-rose-700 border-rose-200",
    open: "bg-amber-100 text-amber-800 border-amber-200",
    purchasing: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    sent: "bg-indigo-100 text-indigo-800 border-indigo-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
    invoiced: "bg-violet-100 text-violet-800 border-violet-200",
    executed: "bg-violet-100 text-violet-800 border-violet-200",
    billed: "bg-amber-100 text-amber-800 border-amber-200",
    paid: "bg-green-100 text-green-800 border-green-200",
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    ordered: "bg-blue-100 text-blue-800 border-blue-200",
    delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  }[s] || "bg-slate-100 text-slate-700 border-slate-200");

export const priorityBadge = (p) =>
  ({
    low: "bg-slate-100 text-slate-600 border-slate-200",
    medium: "bg-sky-100 text-sky-700 border-sky-200",
    high: "bg-orange-100 text-orange-700 border-orange-200",
    urgent: "bg-red-600 text-white border-red-600",
  }[p] || "bg-slate-100 text-slate-600");

export const categoryColor = (c) =>
  ({
    mechanical: "text-orange-600 bg-orange-50",
    electric: "text-yellow-600 bg-yellow-50",
    plumbing: "text-cyan-600 bg-cyan-50",
    drywall: "text-stone-600 bg-stone-50",
    window: "text-sky-600 bg-sky-50",
    landscape: "text-green-600 bg-green-50",
    snow: "text-blue-500 bg-blue-50",
    general: "text-slate-600 bg-slate-50",
  }[c] || "text-slate-600 bg-slate-50");

export const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
